import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { TimeField } from "./pickers";
import { useAccess } from "./access";
import { loadSequences, RELANCE_KINDS, saveSequences, type RelanceSequence, type RelanceStep } from "./relances";
import { useLive } from "./Suivi";
import type { SuiviTask, TaskKind } from "./suivi";

export function RelanceBoard() {
  const { suivi, error, run } = useLive();
  const { canWrite } = useAccess();
  const [sequences, setSequences] = useState<RelanceSequence[]>([]);
  const [ready, setReady] = useState(false);
  const [title, setTitle] = useState("Relancer");
  const [days, setDays] = useState(2);
  const [hour, setHour] = useState("09:00");
  const [kind, setKind] = useState<TaskKind>("call");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const open = (suivi?.tasks || []).filter((task) => !task.done && task.due_on);
  const today = todayIso();

  useEffect(() => {
    let alive = true;
    void loadSequences()
      .then((rows) => { if (alive) setSequences(rows); })
      .catch(() => { if (alive) setSequences([]); })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  async function pose() {
    if (busy) return;
    setBusy(true);
    try {
      await run({ action: "relance", title: title.trim() || "Relancer", days, kind, time: hour });
    } finally {
      setBusy(false);
    }
  }

  async function launch(code: string) {
    if (busy) return;
    setBusy(true);
    try {
      await run({ action: "sequence", code });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="crm-card overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]">
      <h2 className="crm-head flex items-center gap-2 border-b px-4 py-2 text-sm font-semibold">
        Relances
        {canWrite ? <button type="button" className="crm-pill ml-auto rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-50" disabled={!ready} onClick={() => setEditing(true)}>Séquences</button> : null}
      </h2>
      <div className="grid gap-3 p-4">
        {open.length === 0 ? <p className="text-sm text-slate-400">Aucune relance posée.</p> : (
          <ul className="grid gap-1.5">
            {open.map((task) => <RelanceRow key={task.id} task={task} today={today} sequences={sequences} busy={busy} canWrite={canWrite} onDone={() => void run({ action: "done", task_id: task.id, done: 1 })} />)}
          </ul>
        )}
        {canWrite ? (
        <div className="flex flex-wrap items-center gap-2">
          <input className="crm-field h-9 min-w-40 flex-1 rounded-lg border px-2 text-sm" value={title} placeholder="Intitulé" onChange={(event) => setTitle(event.target.value)} />
          <select className="crm-field h-9 rounded-lg border px-2 text-sm" value={kind} aria-label="Type" onChange={(event) => setKind(event.target.value as TaskKind)}>
            {RELANCE_KINDS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <label className="flex items-center gap-1 text-sm text-slate-600">
            J+
            <input className="crm-field h-9 w-16 rounded-lg border px-2 text-sm" type="number" min={0} max={365} value={days} onChange={(event) => setDays(Math.max(0, Math.min(365, Number(event.target.value) || 0)))} />
          </label>
          <div className="w-40 shrink-0">
            <TimeField value={hour} step={15} onChange={setHour} />
          </div>
          <button type="button" className="rounded-full crm-fill px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void pose()}>Poser</button>
        </div>
        ) : null}
        {sequences.length > 0 ? (
          <ul className="grid gap-1.5">
            {sequences.map((sequence) => (
              <li key={sequence.code} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{sequence.name}</p>
                  <p className="truncate text-xs text-slate-500">{sequence.steps.map((step) => `J+${step.days} ${step.time || "09:00"}`).join(" · ")}</p>
                </div>
                {canWrite ? <button type="button" className="crm-pill rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-50" disabled={busy} onClick={() => void launch(sequence.code)}>Lancer</button> : null}
              </li>
            ))}
          </ul>
        ) : null}
        {error ? <p className="text-xs text-rose-700">{error}</p> : null}
      </div>
      {editing ? (
        <Modal onClose={() => setEditing(false)} sheetClass="crm-pop max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl border border-slate-200 p-5">
          {(close) => <SequenceSheet sequences={sequences} onClose={close} onSaved={setSequences} />}
        </Modal>
      ) : null}
    </section>
  );
}

function RelanceRow({ task, today, sequences, busy, canWrite, onDone }: { task: SuiviTask; today: string; sequences: RelanceSequence[]; busy: boolean; canWrite: boolean; onDone: () => void }) {
  const kind = RELANCE_KINDS.find((item) => item.value === task.kind)?.label || "Appeler";
  const sequence = sequences.find((item) => item.code === task.sequence_code)?.name || "";
  const when = task.due_on < today ? "En retard" : task.due_on === today ? "Aujourd’hui" : frDate(task.due_on);
  const clock = task.remind_time ? task.remind_time.slice(0, 5) : "";
  return (
    <li className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{task.title}</p>
        <p className={task.due_on < today ? "truncate text-xs font-semibold text-rose-700" : "truncate text-xs text-slate-500"}>{[kind, when, clock, sequence].filter(Boolean).join(" · ")}</p>
      </div>
      {canWrite ? <button type="button" className="crm-pill rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-50" disabled={busy} onClick={onDone}>Fait</button> : null}
    </li>
  );
}

function SequenceSheet({ sequences, onClose, onSaved }: { sequences: RelanceSequence[]; onClose: () => void; onSaved: (rows: RelanceSequence[]) => void }) {
  const [rows, setRows] = useState(sequences.length > 0 ? sequences : [blankSequence()]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  function patch(code: string, next: Partial<RelanceSequence>) {
    setRows(rows.map((item) => (item.code === code ? { ...item, ...next } : item)));
  }
  function patchStep(code: string, index: number, next: Partial<RelanceStep>) {
    setRows(rows.map((item) => item.code === code ? { ...item, steps: item.steps.map((step, at) => at === index ? { ...step, ...next } : step) } : item));
  }
  function addStep(code: string) {
    setRows(rows.map((item) => item.code === code && item.steps.length < 8 ? { ...item, steps: [...item.steps, { days: 7, kind: "call", title: "Relancer", time: "09:00" }] } : item));
  }
  function removeStep(code: string, index: number) {
    setRows(rows.map((item) => item.code === code && item.steps.length > 1 ? { ...item, steps: item.steps.filter((_, at) => at !== index) } : item));
  }
  function add() {
    if (rows.length >= 8) {
      setError("Huit séquences au maximum.");
      return;
    }
    setRows([...rows, blankSequence()]);
    setError("");
  }
  function remove(code: string) {
    if (rows.length <= 1) return;
    setRows(rows.filter((item) => item.code !== code));
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      const saved = await saveSequences(rows);
      onSaved(saved);
      onClose();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Séquences non enregistrées");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="grid gap-3">
      <h2 className="text-base font-semibold">Séquences de relance</h2>
      <ul className="grid gap-3">
        {rows.map((sequence) => (
          <li key={sequence.code} className="grid gap-2 rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <input className="crm-field h-8 min-w-0 flex-1 rounded-lg border px-2 text-sm" value={sequence.name} aria-label="Nom de la séquence" onChange={(event) => patch(sequence.code, { name: event.target.value })} />
              <button type="button" className="crm-pill h-8 rounded-full px-2 text-xs font-semibold text-rose-700" disabled={rows.length <= 1} onClick={() => remove(sequence.code)}>Retirer</button>
            </div>
            {sequence.steps.map((step, index) => (
              <div key={`${sequence.code}-${index}`} className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-slate-600">
                  J+
                  <input className="crm-field h-8 w-16 rounded-lg border px-2 text-sm" type="number" min={0} max={365} value={step.days} onChange={(event) => patchStep(sequence.code, index, { days: Math.max(0, Math.min(365, Number(event.target.value) || 0)) })} />
                </label>
                <select className="crm-field h-8 rounded-lg border px-2 text-sm" value={step.kind} aria-label="Type" onChange={(event) => patchStep(sequence.code, index, { kind: event.target.value as TaskKind })}>
                  {RELANCE_KINDS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
                <div className="w-40 shrink-0">
                  <TimeField value={step.time || "09:00"} step={15} onChange={(time) => patchStep(sequence.code, index, { time })} />
                </div>
                <input className="crm-field h-8 min-w-32 flex-1 rounded-lg border px-2 text-sm" value={step.title} placeholder="Intitulé" onChange={(event) => patchStep(sequence.code, index, { title: event.target.value })} />
                <button type="button" className="crm-pill h-8 rounded-full px-2 text-xs font-semibold text-rose-700" disabled={sequence.steps.length <= 1} onClick={() => removeStep(sequence.code, index)}>Retirer</button>
              </div>
            ))}
            <button type="button" className="crm-pill w-fit rounded-full px-3 py-1 text-xs font-semibold" disabled={sequence.steps.length >= 8} onClick={() => addStep(sequence.code)}>Ajouter un jour</button>
          </li>
        ))}
      </ul>
      {error ? <p className="text-xs text-rose-700">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="crm-pill rounded-full px-3 py-1.5 text-xs font-semibold" disabled={rows.length >= 8} onClick={add}>Ajouter</button>
        <button type="button" className="rounded-full crm-fill px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void save()}>{busy ? "…" : "Enregistrer"}</button>
      </div>
    </div>
  );
}

function blankSequence(): RelanceSequence {
  return { code: `r${Math.random().toString(36).slice(2, 8)}`, name: "Nouvelle séquence", steps: [{ days: 2, kind: "call", title: "Relancer", time: "09:00" }] };
}

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function frDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}
